import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useChatStore, MCPToolCall } from '../stores/chatStore'
import { useMCPStore } from '../stores/mcpStore'
import { useMultiAgentStore, AgentRole, AgentStatus } from '../stores/multiAgentStore'
import { mcpApi, githubApi, type ClonedRepo } from '../services/api'
import { 
  Bot, Send, Sparkles, Layers, Plus, 
  Menu, X, Search, Trash2, MessageCircle,
  Copy, Check, Users, CheckCircle, XCircle, Brain, Archive,
  FolderGit, Database, FileCode, ChevronDown, Terminal, Loader2, AlertCircle, Cloud
} from 'lucide-react'
import { format } from 'date-fns'
import ReactMarkdown from 'react-markdown'
import Prism from 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-scss'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-markdown'
import type { Message, Conversation } from '../types'
import UpgradeBanner from '../components/UpgradeBanner'

// Agent configuration
const agentConfig: Record<string, { name: string; color: string; icon: string }> = {
  architect: { name: 'Architect', color: 'blue', icon: '🏗️' },
  developer: { name: 'Developer', color: 'green', icon: '👨‍💻' },
  qa: { name: 'QA Engineer', color: 'purple', icon: '🔍' },
}

// Message Component
function MessageBubble({ 
  message, 
  isStreaming = false,
  mcpToolCalls = [],
  selectedMcpServer = null,
  onMCPToolRetry
}: { 
  message: { id: string; role: string; content: string; timestamp: string; provider?: string }
  isStreaming?: boolean
  mcpToolCalls?: MCPToolCall[]
  selectedMcpServer?: string | null
  onMCPToolRetry?: (toolCall: MCPToolCall) => void
}) {
  const isUser = message.role === 'user'
  const [copied, setCopied] = useState(false)
  const { isStreaming: globalIsStreaming } = useChatStore()

  const copyToClipboard = () => {
    navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Show streaming indicator for the last assistant message when global streaming is active
  const showStreaming = isStreaming || (message.role === 'assistant' && globalIsStreaming && message.id === 'streaming')

  // Parse and strip MCP tool calls from content for display
  const parseAndStripMCPToolCalls = (content: string) => {
    const toolCallRegex = /<mcp>\s*<tool-call>\s*<tool-name>(.*?)<\/tool-name>\s*<tool-params>(.*?)<\/tool-params>\s*<\/tool-call>\s*<\/mcp>/g
    return content.replace(toolCallRegex, '').trim()
  }

  const displayContent = parseAndStripMCPToolCalls(message.content)
  const hasMCPToolCalls = mcpToolCalls && mcpToolCalls.length > 0

  return (
    <div className={`flex gap-4 ${isUser ? 'flex-row-reverse' : ''} animate-fade-in`}>
      {/* Avatar */}
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg ${
        isUser ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-primary-500 to-primary-600'
      }`}>
        {isUser ? (
          <span className="text-sm font-semibold text-white">U</span>
        ) : (
          <Bot className="w-5 h-5 text-white" />
        )}
      </div>

      {/* Message */}
      <div className={`flex-1 max-w-3xl ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block rounded-2xl px-5 py-3 text-base leading-relaxed ${
          isUser 
            ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-br-md shadow-lg shadow-primary-500/20' 
            : 'bg-dark-800/95 text-dark-100 rounded-bl-md shadow-lg border border-dark-700/50'
        }`}>
          <div className="prose prose-invert prose-sm max-w-none">
            <ReactMarkdown
              components={{
                code({ node, inline, className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || '')
                  const language = match?.[1] || 'text'
                  
                  // Use Prism.js for syntax highlighting
                  useEffect(() => {
                    if (!inline && className) {
                      Prism.highlightAll()
                    }
                  }, [children, className, inline])
                  
                  return !inline ? (
                    <div className="relative group my-4">
                      <div className="flex items-center justify-between bg-dark-900/80 backdrop-blur-sm px-4 py-2 rounded-t-lg border border-dark-700/50">
                        <span className="text-xs text-dark-400 uppercase tracking-wide">{language}</span>
                        <button 
                          onClick={copyToClipboard}
                          className="p-1 hover:bg-dark-700 rounded transition-colors"
                        >
                          {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 text-dark-400" />}
                        </button>
                      </div>
                      <pre className="!bg-dark-900/80 !backdrop-blur-sm !border-x !border-b !border-dark-700/50 !rounded-b-lg !rounded-tr-none !p-4 overflow-x-auto">
                        <code className={`${className || ''} language-${language}`} {...props}>{children}</code>
                      </pre>
                    </div>
                  ) : (
                    <code className={`${className || ''} bg-dark-700/50 px-1.5 py-0.5 rounded text-primary-300`} {...props}>{children}</code>
                  )
                }
              }}
            >
              {displayContent || (isUser ? message.content : '')}
            </ReactMarkdown>
          </div>
          
          {/* MCP Tool Calls Display */}
          {hasMCPToolCalls && (
            <div className="mt-4 space-y-2">
              {mcpToolCalls.map((toolCall) => (
                <MCPToolCallDisplay
                  key={toolCall.id}
                  toolCall={toolCall}
                  serverId={selectedMcpServer}
                  onRetry={() => onMCPToolRetry?.(toolCall)}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Meta */}
        <div className={`flex items-center gap-2 mt-2 text-xs font-medium text-dark-500 ${isUser ? 'justify-end' : ''}`}>
          <span className="opacity-70">{message.timestamp ? format(new Date(message.timestamp), 'HH:mm') : ''}</span>
          {!isUser && message.provider && (
            <span className="px-2 py-0.5 bg-dark-700/80 rounded text-dark-300 uppercase tracking-wide">
              {message.provider}
            </span>
          )}
          {showStreaming && (
            <span className="text-primary-400 flex items-center gap-1.5 animate-pulse">
              <span className="w-2 h-2 bg-primary-400 rounded-full"></span>
              <span className="tracking-wide">generating...</span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Conversation Item Component
function ConversationItem({ 
  conversation, 
  isActive, 
  onClick, 
  onDelete 
}: { 
  conversation: { id: string; title: string; mode: string; updatedAt: string }
  isActive: boolean
  onClick: () => void
  onDelete: (e: React.MouseEvent) => void
}) {
  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'agentic': return <Sparkles className="w-3 h-3" />
      case 'multi-agent': return <Layers className="w-3 h-3" />
      default: return <MessageCircle className="w-3 h-3" />
    }
  }

  return (
    <div 
      onClick={onClick}
      className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
        isActive ? 'bg-dark-700' : 'hover:bg-dark-800'
      }`}
    >
      <div className="text-dark-400">{getModeIcon(conversation.mode)}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-dark-200 truncate">{conversation.title}</p>
        <p className="text-xs text-dark-500">{format(new Date(conversation.updatedAt), 'MMM d, HH:mm')}</p>
      </div>
      <button 
        onClick={onDelete}
        className="p-1.5 text-dark-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// Context Mode Selector Component
function ContextModeSelector({
  mode,
  setMode,
  selectedGitRepo,
  setSelectedGitRepo,
  selectedMcpServer,
  setSelectedMcpServer,
  selectedCodebaseId,
  setSelectedCodebaseId,
  codebases,
  mcpServers
}: {
  mode: 'standard' | 'git' | 'mcp'
  setMode: (mode: 'standard' | 'git' | 'mcp') => void
  selectedGitRepo: string | null
  setSelectedGitRepo: (repo: string | null) => void
  selectedMcpServer: string | null
  setSelectedMcpServer: (server: string | null) => void
  selectedCodebaseId: string | null
  setSelectedCodebaseId: (codebaseId: string | null) => void
  codebases: Array<{ id: string; name: string; rootPath: string; indexStatus: string }>
  mcpServers: Array<{ id: string; name: string }>
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [clonedRepos, setClonedRepos] = useState<ClonedRepo[]>([])
  const [loadingRepos, setLoadingRepos] = useState(false)
  
  const modeConfig = {
    standard: { icon: FileCode, label: 'Standard', color: 'text-gray-400', desc: 'Code snippets in chat' },
    git: { icon: FolderGit, label: 'Git Repo', color: 'text-orange-400', desc: 'Real file operations' },
    mcp: { icon: Database, label: 'MCP', color: 'text-purple-400', desc: 'MCP tools & APIs' }
  }

  const config = modeConfig[mode]

  // Get the selected codebase name for display
  const selectedCodebaseName = selectedCodebaseId
    ? codebases.find(c => c.id === selectedCodebaseId)?.name
    : null

  // Determine what to show in the button
  const buttonLabel = mode === 'standard' && selectedCodebaseName
    ? selectedCodebaseName
    : (mode === 'standard' ? 'CODEBASE' : config.label)
  
  // Fetch cloned repos when switching to git mode
  useEffect(() => {
    if (mode === 'git' && isOpen && clonedRepos.length === 0) {
      fetchClonedRepos()
    }
  }, [mode, isOpen])
  
  const fetchClonedRepos = async () => {
    setLoadingRepos(true)
    try {
      const response = await githubApi.getClonedRepos()
      setClonedRepos(response.data)
    } catch (error) {
      console.error('Failed to fetch cloned repos:', error)
      setClonedRepos([])
    } finally {
      setLoadingRepos(false)
    }
  }
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-dark-800 hover:bg-dark-700 rounded-lg text-sm transition-colors border border-dark-600"
      >
        <config.icon className={`w-4 h-4 ${config.color}`} />
        <span className="text-dark-200">
          {buttonLabel}
        </span>
        <ChevronDown className={`w-3 h-3 text-dark-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute bottom-full left-0 mb-2 w-72 bg-dark-800 rounded-lg border border-dark-600 shadow-xl z-20 overflow-hidden">
            {/* Standard Mode */}
            <button
              onClick={() => { setMode('standard'); setIsOpen(false) }}
              className={`w-full flex items-start gap-3 p-3 hover:bg-dark-700 transition-colors ${mode === 'standard' ? 'bg-dark-700/50' : ''}`}
            >
              <FileCode className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="text-left">
                <div className="text-sm font-medium text-dark-200">Standard</div>
                <div className="text-xs text-dark-500">Code snippets in chat, copy or download ZIP</div>
              </div>
            </button>
            
            {/* Git Repo Mode */}
            <button
              onClick={() => { setMode('git'); setIsOpen(false) }}
              className={`w-full flex items-start gap-3 p-3 hover:bg-dark-700 transition-colors ${mode === 'git' ? 'bg-dark-700/50' : ''}`}
            >
              <FolderGit className="w-5 h-5 text-orange-400 mt-0.5" />
              <div className="text-left">
                <div className="text-sm font-medium text-dark-200">Git Repository</div>
                <div className="text-xs text-dark-500">Real file read/write, commit, push, pull</div>
              </div>
            </button>
            
            {/* MCP Mode */}
            <button
              onClick={() => { setMode('mcp'); setIsOpen(false) }}
              className={`w-full flex items-start gap-3 p-3 hover:bg-dark-700 transition-colors ${mode === 'mcp' ? 'bg-dark-700/50' : ''}`}
            >
              <Database className="w-5 h-5 text-purple-400 mt-0.5" />
              <div className="text-left">
                <div className="text-sm font-medium text-dark-200">MCP Tools</div>
                <div className="text-xs text-dark-500">Use MCP servers for file ops, APIs, databases</div>
              </div>
            </button>
            
            {/* Selected Codebase (available in standard mode) */}
            {mode === 'standard' && (
              <div className="border-t border-dark-600 p-3">
                <div className="text-xs text-dark-500 mb-2">Select Codebase</div>
                <select
                  value={selectedCodebaseId || ''}
                  onChange={(e) => setSelectedCodebaseId(e.target.value || null)}
                  className="w-full bg-dark-700 border border-dark-600 rounded px-2 py-1.5 text-sm text-dark-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">No codebase (general chat)</option>
                  {codebases.map(codebase => (
                    <option key={codebase.id} value={codebase.id}>
                      {codebase.name} {codebase.indexStatus === 'complete' ? '✓' : '⏳'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Selected Git Repo (if in git mode) */}
            {mode === 'git' && (
              <div className="border-t border-dark-600 p-3">
                <div className="text-xs text-dark-500 mb-2">Select Repository</div>
                <select
                  value={selectedGitRepo || ''}
                  onChange={(e) => setSelectedGitRepo(e.target.value || null)}
                  className="w-full bg-dark-700 border border-dark-600 rounded px-2 py-1.5 text-sm text-dark-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">{loadingRepos ? 'Loading...' : 'Choose a repository...'}</option>
                  {clonedRepos.map(repo => (
                    <option key={repo.name} value={repo.name}>
                      {repo.name} {repo.branch && `(${repo.branch})`}
                    </option>
                  ))}
                  {!loadingRepos && clonedRepos.length === 0 && (
                    <option value="halcode-webapp">halcode-webapp</option>
                  )}
                </select>
                {clonedRepos.length === 0 && !loadingRepos && (
                  <div className="mt-2 text-xs text-dark-500">
                    No cloned repos. Visit <a href="/git" className="text-primary-400 hover:underline">Git page</a> to clone a repository.
                  </div>
                )}
              </div>
            )}
            
            {/* Selected MCP Server (if in mcp mode) */}
            {mode === 'mcp' && (
              <div className="border-t border-dark-600 p-3">
                <div className="text-xs text-dark-500 mb-2">Select MCP Server</div>
                <select
                  value={selectedMcpServer || ''}
                  onChange={(e) => setSelectedMcpServer(e.target.value || null)}
                  className="w-full bg-dark-700 border border-dark-600 rounded px-2 py-1.5 text-sm text-dark-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Choose a server...</option>
                  {mcpServers.map(server => (
                    <option key={server.id} value={server.id}>{server.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// MCP Tool Call Component
function MCPToolCallDisplay({ 
  toolCall, 
  serverId, 
  onRetry 
}: { 
  toolCall: MCPToolCall
  serverId: string | null
  onRetry: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  
  const getStatusIcon = () => {
    switch (toolCall.status) {
      case 'pending':
        return <ClockIcon />
      case 'executing':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-400" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />
    }
  }
  
  const getStatusColor = () => {
    switch (toolCall.status) {
      case 'pending': return 'border-yellow-500/50 bg-yellow-500/10'
      case 'executing': return 'border-blue-500/50 bg-blue-500/10'
      case 'success': return 'border-green-500/50 bg-green-500/10'
      case 'error': return 'border-red-500/50 bg-red-500/10'
    }
  }
  
  return (
    <div className={`mt-3 rounded-lg border ${getStatusColor()} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-3 hover:bg-dark-800/50 transition-colors"
      >
        {getStatusIcon()}
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-sm font-medium text-dark-200">{toolCall.toolName}</span>
          </div>
          <div className="text-xs text-dark-500 mt-0.5">
            {toolCall.status === 'executing' && 'Executing...'}
            {toolCall.status === 'success' && 'Completed successfully'}
            {toolCall.status === 'error' && `Error: ${toolCall.error}`}
            {toolCall.status === 'pending' && 'Pending'}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-dark-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Parameters */}
          <div>
            <div className="text-xs text-dark-500 mb-1">Parameters</div>
            <pre className="bg-dark-900 rounded px-3 py-2 text-xs text-dark-300 overflow-x-auto">
              {JSON.stringify(toolCall.params, null, 2)}
            </pre>
          </div>
          
          {/* Result */}
          {(toolCall.status === 'success' || toolCall.status === 'error') && (
            <div>
              <div className="text-xs text-dark-500 mb-1">
                {toolCall.status === 'success' ? 'Result' : 'Error'}
              </div>
              <pre className={`rounded px-3 py-2 text-xs overflow-x-auto ${
                toolCall.status === 'success' 
                  ? 'bg-green-900/20 text-green-200' 
                  : 'bg-red-900/20 text-red-200'
              }`}>
                {typeof toolCall.result === 'object' 
                  ? JSON.stringify(toolCall.result, null, 2)
                  : toolCall.result || toolCall.error}
              </pre>
            </div>
          )}
          
          {/* Retry Button */}
          {toolCall.status === 'error' && serverId && (
            <button
              onClick={(e) => { e.stopPropagation(); onRetry() }}
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-dark-700 hover:bg-dark-600 rounded transition-colors"
            >
              <Loader2 className="w-3 h-3" />
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ClockIcon() {
  return (
    <svg className="w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

// Multi-Agent Panel Component
function MultiAgentPanel() {
  const { user } = useAuthStore()
  const { 
    sessions, currentSession, loading, error,
    fetchSessions, fetchSession, createSession,
    runArchitectPhase, approvePlan, runDeveloperPhase,
    runQAPhase, approveFinal, rejectSession, cancelSession,
    clearCurrentSession, clearError
  } = useMultiAgentStore()

  const [newRequest, setNewRequest] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  const handleCreateSession = async () => {
    if (!newRequest.trim()) return
    const session = await createSession(newRequest, 'default-vfs')
    if (session) {
      setNewRequest('')
      await runArchitectPhase(session.id)
    }
  }

  const handleApprovePlan = async () => {
    if (!currentSession) return
    await approvePlan(currentSession.id)
    await runDeveloperPhase(currentSession.id)
  }

  const handleApproveFinal = async () => {
    if (!currentSession) return
    await approveFinal(currentSession.id)
  }

  const handleReject = async () => {
    if (!currentSession || !rejectReason.trim()) return
    await rejectSession(currentSession.id, rejectReason)
    setShowRejectModal(false)
    setRejectReason('')
  }

  const getStatusBadge = (status: AgentStatus) => {
    const config: Record<AgentStatus, { label: string; color: string }> = {
      idle: { label: 'Idle', color: 'gray' },
      thinking: { label: 'Thinking', color: 'yellow' },
      working: { label: 'Working', color: 'blue' },
      reviewing: { label: 'Reviewing', color: 'purple' },
      completed: { label: 'Completed', color: 'green' },
      error: { label: 'Error', color: 'red' },
    }
    const c = config[status]
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
    }
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[c.color]}`}>{c.label}</span>
  }

  return (
    <div className="h-full flex">
      {/* Left Panel - Session List */}
      <div className="w-72 border-r border-dark-700 flex flex-col">
        {/* New Session */}
        <div className="p-4 border-b border-dark-700">
          <textarea
            value={newRequest}
            onChange={(e) => setNewRequest(e.target.value)}
            placeholder="Describe what you want to build..."
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            rows={3}
          />
          <button
            onClick={handleCreateSession}
            disabled={loading || !newRequest.trim()}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:bg-dark-600"
          >
            <Users className="w-4 h-4" />
            Start Multi-Agent
          </button>
        </div>

        {/* Session List */}
        <div className="flex-1 overflow-y-auto p-2">
          {sessions.length === 0 ? (
            <div className="text-center text-dark-500 text-sm py-4">No sessions yet</div>
          ) : (
            <div className="space-y-2">
              {sessions.filter(Boolean).slice(0, 10).map((session) => (
                <button
                  key={session.id}
                  onClick={() => fetchSession(session.id)}
                  className={`w-full p-3 rounded-lg border text-left transition-colors ${
                    currentSession?.id === session.id
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-dark-700 hover:bg-dark-800'
                  }`}
                >
                  <p className="text-sm font-medium text-dark-200 truncate">{session.userRequest}</p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-dark-500">
                      {new Date(session.createdAt).toLocaleDateString()}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      session.status === 'completed' ? 'bg-green-100 text-green-800' :
                      session.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {session.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Active Session */}
      <div className="flex-1 flex flex-col min-w-0">
        {!currentSession ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">🤖</div>
              <h2 className="text-xl font-semibold text-dark-200 mb-2">Multi-Agent Workflow</h2>
              <p className="text-dark-500">Create a session to start collaborating with 3 AI agents</p>
            </div>
          </div>
        ) : (
          <>
            {/* Session Header */}
            <div className="p-4 border-b border-dark-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-semibold text-dark-200">{currentSession.userRequest}</h2>
                  <p className="text-sm text-dark-500">{currentSession.currentPhase}</p>
                </div>
                {currentSession.status !== 'completed' && currentSession.status !== 'cancelled' && (
                  <button
                    onClick={() => cancelSession(currentSession.id)}
                    className="px-3 py-1 text-sm text-red-400 hover:text-red-300"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {/* Progress */}
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-dark-400">Implementation Progress</span>
                  <span className="text-dark-400">{currentSession.implementationProgress}%</span>
                </div>
                <div className="w-full bg-dark-700 rounded-full h-2">
                  <div 
                    className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${currentSession.implementationProgress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Agent Cards */}
            <div className="p-4 grid grid-cols-3 gap-4">
              {(Object.keys(agentConfig) as AgentRole[]).map((role) => {
                const agent = currentSession.agents?.[role] || { status: 'idle' as const, taskCount: 0, completedTasks: 0 }
                const config = agentConfig[role]
                return (
                  <div
                    key={role}
                    className={`p-4 rounded-lg border ${
                      agent.status === 'thinking' || agent.status === 'working' || agent.status === 'reviewing'
                        ? 'border-primary-500 bg-primary-500/5'
                        : 'border-dark-700 bg-dark-800'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{config.icon}</span>
                      <div>
                        <h3 className="font-medium text-dark-200">{config.name}</h3>
                        {getStatusBadge(agent.status)}
                      </div>
                    </div>
                    {agent.taskCount > 0 && (
                      <p className="text-xs text-dark-500">
                        {agent.completedTasks}/{agent.taskCount} tasks
                      </p>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Architecture Plan */}
            {currentSession.architecturePlan && currentSession.status === 'awaiting_plan_approval' && (
              <div className="p-4 border-t border-dark-700">
                <h3 className="font-medium text-dark-200 mb-3">📋 Architecture Plan</h3>
                <div className="bg-dark-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <p className="text-sm text-dark-300 mb-3">{currentSession.architecturePlan.overview}</p>
                  <div className="space-y-2">
                    {currentSession.architecturePlan.fileStructure.slice(0, 8).map((file: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded ${
                          file.action === 'create' ? 'bg-green-900 text-green-200' :
                          file.action === 'modify' ? 'bg-yellow-900 text-yellow-200' :
                          'bg-red-900 text-red-200'
                        }`}>{file.action}</span>
                        <span className="text-dark-300 font-mono">{file.path}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={handleApprovePlan}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve & Implement
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            )}

            {/* Implementation Results */}
            {(currentSession.filesModified > 0 || currentSession.filesCreated > 0) && currentSession.status === 'awaiting_final_approval' && (
              <div className="p-4 border-t border-dark-700">
                <h3 className="font-medium text-dark-200 mb-3">📁 Implementation Complete</h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-green-900/30 p-3 rounded-lg text-center">
                    <div className="text-xl font-bold text-green-400">{currentSession.filesCreated}</div>
                    <div className="text-xs text-green-300">Created</div>
                  </div>
                  <div className="bg-yellow-900/30 p-3 rounded-lg text-center">
                    <div className="text-xl font-bold text-yellow-400">{currentSession.filesModified}</div>
                    <div className="text-xs text-yellow-300">Modified</div>
                  </div>
                  <div className="bg-red-900/30 p-3 rounded-lg text-center">
                    <div className="text-xl font-bold text-red-400">{currentSession.filesDeleted}</div>
                    <div className="text-xs text-red-300">Deleted</div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleApproveFinal}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve Final
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {currentSession.messages.map((message) => {
                const config = agentConfig[message.agent] || agentConfig.architect
                return (
                  <div key={message.id} className="flex gap-3">
                    <span className="text-xl">{config.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-dark-200">{message.agentName}</span>
                        <span className="text-xs text-dark-500">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-dark-300 mt-1 whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-dark-200 mb-4">Reject Session</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-dark-200 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-dark-600 text-dark-300 rounded-lg hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-dark-600"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Main Chat Page
export default function ChatPage() {
  const { user } = useAuthStore()
  const {
    conversations,
    currentConversation,
    messages,
    isStreaming,
    streamingContent,
    chatMode,
    selectedProvider,
    isSidebarOpen,
    searchQuery,
    contextMode,
    selectedGitRepo,
    selectedMcpServer,
    selectedCodebaseId,
    availableCodebases,
    mcpToolCalls,
    executeMCPTool,
    fetchConversations,
    createConversation,
    selectConversation,
    deleteConversation,
    sendMessage,
    cancelStreaming,
    setChatMode,
    setSelectedProvider,
    toggleSidebar,
    setSearchQuery,
    clearCurrentConversation,
    setContextMode,
    setSelectedGitRepo,
    setSelectedMcpServer,
    setSelectedCodebaseId,
    fetchAvailableCodebases,
    selectedModel,
    setSelectedModel
  } = useChatStore()
  
  // Get MCP servers for context mode
  const { servers } = useMCPStore()
  const mcpServers = servers.filter(s => s.status === 'connected')

  const [message, setMessage] = useState('')
  const [inputKey, setInputKey] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-detect provider based on user settings
  useEffect(() => {
    if (user && !selectedProvider) {
      // Prefer Gemini if set, otherwise Claude
      if (user.googleApiKey) {
        setSelectedProvider('gemini')
      } else if (user.anthropicApiKey) {
        setSelectedProvider('claude')
      }
    }
  }, [user, selectedProvider])

  // Auto-switch model when provider changes
  useEffect(() => {
    if (selectedProvider === 'claude') {
      setSelectedModel('claude-sonnet-4-20250514')
    } else if (selectedProvider === 'gemini') {
      setSelectedModel('gemini-2.5-pro')
    } else if (selectedProvider === 'groq') {
      setSelectedModel('moonshotai/kimi-k2-instruct-0905')
    } else if (selectedProvider === 'ollama') {
      setSelectedModel('llama3.1')
    }
  }, [selectedProvider])

  // Fetch conversations on mount
  useEffect(() => {
    if (user?.id) {
      fetchConversations(user.id)
      fetchAvailableCodebases()
    }
  }, [user?.id])

  // Scroll to bottom when messages change (smart scroll - only if user is near bottom)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const isAutoScrolling = useRef(false)

  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      // Check if user is near bottom (within 50px)
      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceToBottom = scrollHeight - scrollTop - clientHeight
      isAutoScrolling.current = distanceToBottom < 50
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isAutoScrolling.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, streamingContent])

  const handleSend = async () => {
    if (!message.trim() || isStreaming) return

    const userId = user?.id
    if (!userId) return

    await sendMessage(userId, message.trim())
    setMessage('')
    setInputKey(Date.now().toString())
  }

  const handleMCPToolRetry = async (toolCall: MCPToolCall) => {
    if (!selectedMcpServer) return
    await executeMCPTool(selectedMcpServer, toolCall.toolName, toolCall.params)
  }

  const handleNewChat = async () => {
    const userId = user?.id
    if (!userId) return

    clearCurrentConversation()
    await createConversation(userId, 'New Chat', chatMode)
  }

  const handleDownloadZip = async () => {
    if (!currentConversation?.id) return
    
    try {
      const token = localStorage.getItem('halcode-auth')
      const accessToken = token ? JSON.parse(token).state?.accessToken || JSON.parse(token).accessToken : null
      
      if (!accessToken) {
        alert('Please log in again')
        return
      }
      
      const response = await fetch('/api/chat/download-zip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ conversationId: currentConversation.id })
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Download failed')
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${currentConversation.title.replace(/[^a-z0-9]/gi, '_')}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      console.error('Download error:', error)
      alert(error.message || 'Failed to download ZIP')
    }
  }

  const filteredConversations = conversations.filter(c => 
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Render multi-agent panel if mode is multi-agent
  if ((chatMode as any) === 'multi-agent') {
    return <MultiAgentPanel />
  }
  
  return (
    <div className="h-full flex bg-dark-900">
      {/* Sidebar */}
      <div className={`${isSidebarOpen ? 'w-72' : 'w-0'} flex-shrink-0 border-r border-dark-700 flex flex-col transition-all duration-300 overflow-hidden`}>
        {/* Header */}
        <div className="p-4 border-b border-dark-700">
          <button 
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Search */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-dark-800 border border-dark-600 rounded-lg pl-9 pr-3 py-2 text-sm text-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredConversations.length === 0 ? (
            <div className="text-center text-dark-500 text-sm py-8">
              No conversations yet
            </div>
          ) : (
            <div className="space-y-1">
              {filteredConversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={currentConversation?.id === conv.id}
                  onClick={() => selectConversation(conv)}
                  onDelete={(e) => {
                    e.stopPropagation()
                    deleteConversation(conv.id)
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Floating Sidebar Toggle - Always Visible */}
        <button 
          onClick={toggleSidebar}
          className={`absolute top-3 z-10 p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors ${
            isSidebarOpen ? 'left-[calc(-3rem-4px)]' : 'left-3'
          }`}
          title={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-dark-700 ml-12">
          <div className="flex items-center gap-1 p-1 bg-dark-800 rounded-lg">
            <button
              onClick={() => setSelectedProvider('claude')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedProvider === 'claude' 
                  ? 'bg-orange-500/10 text-orange-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
              title="Claude"
            >
              <Brain className="w-3.5 h-3.5" />
              Claude
            </button>
            <button
              onClick={() => setSelectedProvider('gemini')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedProvider === 'gemini' 
                  ? 'bg-blue-500/10 text-blue-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
              title="Gemini"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Gemini
            </button>
            <button
              onClick={() => {
                setSelectedProvider('ollama');
                setSelectedModel('llama3.1'); // Default to Ollama Cloud model
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedProvider === 'ollama' 
                  ? 'bg-purple-500/10 text-purple-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
              title="Ollama Local"
            >
              <Cloud className="w-3.5 h-3.5" />
              Ollama
            </button>
            <button
              onClick={() => {
                setSelectedProvider('groq');
                setSelectedModel('moonshotai/kimi-k2-instruct-0905');
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedProvider === 'groq' 
                  ? 'bg-teal-500/10 text-teal-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
              title="Groq (Free)"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Groq (Kimi)
            </button>
          </div>

          {/* Model Selector */}
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="bg-dark-700 text-white text-sm rounded px-2 py-1.5 border border-dark-600 focus:border-blue-500 focus:outline-none"
          >
            {/* Anthropic Models */}
            <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
            <option value="claude-opus-4-20250514">Claude Opus 4</option>
            <option value="claude-haiku-3-20250514">Claude Haiku 3</option>
            {/* Google Models */}
            <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
            <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
            {/* Ollama Cloud Models (require API key from ollama.com) */}
            <option value="llama3.1">🟢 Llama 3.1 (Cloud)</option>
            <option value="llama3.1:70b">🟢 Llama 3.1 70B (Cloud)</option>
            <option value="mistral">🟢 Mistral (Cloud)</option>
            <option value="mixtral:8x22b">🟢 Mixtral 8x22B (Cloud)</option>
            <option value="codellama:7b">🟢 CodeLlama 7B (Cloud)</option>
            {/* Local Ollama Models */}
            <option value="codellama:13b">🔵 CodeLlama 13B (Local)</option>
            <option value="codegemma:7b">🔵 CodeGemma 7B (Local)</option>
            <option value="qwen2.5:7b">🔵 Qwen 2.5 7B (Local)</option>
            <option value="phi3:latest">🔵 Phi-3 (Local)</option>
            <option value="llama3">🔵 Llama 3 (Local)</option>
            {/* Groq Models (Free) */}
            <option value="moonshotai/kimi-k2-instruct-0905">🟢 Kimi K2 (Groq)</option>
            <option value="llama-3.3-70b-versatile">🟢 Llama 3.3 70B (Groq)</option>
            <option value="llama-3.1-8b-instant">🟢 Llama 3.1 8B (Groq)</option>
            <option value="mixtral-8x7b-32768">🟢 Mixtral 8x7B (Groq)</option>
          </select>

          {/* Mode Selector */}
          <div className="flex items-center gap-1 p-1 bg-dark-800 rounded-lg">
            <button
              onClick={() => setChatMode('assistant')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chatMode === 'assistant' 
                  ? 'bg-primary-500/10 text-primary-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              <Bot className="w-4 h-4" />
              Assistant
            </button>
            <button
              onClick={() => setChatMode('agentic')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chatMode === 'agentic' 
                  ? 'bg-primary-500/10 text-primary-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Agentic
            </button>
            <button
              onClick={() => setChatMode('multi-agent')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chatMode === 'multi-agent' 
                  ? 'bg-primary-500/10 text-primary-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4" />
              Multi-Agent
            </button>
          </div>

          {/* New Chat Button (Mobile) */}
          <button 
            onClick={handleNewChat}
            className="ml-auto p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors lg:hidden"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div ref={messagesContainerRef} className="h-[calc(100vh-280px)] overflow-y-auto p-3 space-y-3 bg-dark-900/60 backdrop-blur-md">
          <UpgradeBanner />
          {messages.length === 0 && !isStreaming ? (
            /* Welcome Message */
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 max-w-2xl">
                <div className="bg-dark-800 rounded-xl p-5">
                  <p className="text-dark-200 mb-4">
                    👋 Hi {user?.email?.split('@')[0]}! I'm HalCode, your AI coding assistant.
                  </p>
                  <p className="text-dark-300 mb-4">
                    I can help you with:
                  </p>
                  <ul className="space-y-2 text-dark-300">
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Writing and editing code
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Explaining code and concepts
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Debugging issues
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Refactoring and optimizing
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Agentic & Multi-Agent workflows
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble 
                  key={msg.id} 
                  message={msg} 
                  mcpToolCalls={mcpToolCalls}
                  selectedMcpServer={selectedMcpServer}
                  onMCPToolRetry={handleMCPToolRetry}
                />
              ))}
              
              {isStreaming && streamingContent && (
                <MessageBubble 
                  message={{
                    id: 'streaming',
                    role: 'assistant',
                    content: streamingContent,
                    timestamp: new Date().toISOString()
                  }} 
                  isStreaming
                  mcpToolCalls={mcpToolCalls}
                  selectedMcpServer={selectedMcpServer}
                  onMCPToolRetry={handleMCPToolRetry}
                />
              )}
            </>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-3 border-t border-dark-700/50 bg-dark-800/60 backdrop-blur-lg">
          {/* Context Mode Selector - Only show in assistant mode */}
          {chatMode === 'assistant' && (
            <div className="flex items-center gap-2 mb-2">
              <ContextModeSelector
                mode={contextMode}
                setMode={setContextMode}
                selectedGitRepo={selectedGitRepo}
                setSelectedGitRepo={setSelectedGitRepo}
                selectedMcpServer={selectedMcpServer}
                setSelectedMcpServer={setSelectedMcpServer}
                selectedCodebaseId={selectedCodebaseId}
                setSelectedCodebaseId={setSelectedCodebaseId}
                codebases={availableCodebases}
                mcpServers={mcpServers}
              />
            </div>
          )}

          {/* Codebase Selector for Agentic Mode */}
          {chatMode === 'agentic' && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-dark-800 rounded-lg text-sm border border-dark-600">
                <FileCode className="w-4 h-4 text-gray-400" />
                <span className="text-dark-200">CODEBASE</span>
              </div>
              <select
                value={selectedCodebaseId || ''}
                onChange={(e) => setSelectedCodebaseId(e.target.value || null)}
                className="bg-dark-700 border border-dark-600 rounded px-2 py-1.5 text-sm text-dark-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Select a codebase...</option>
                {availableCodebases.map(codebase => (
                  <option key={codebase.id} value={codebase.id}>
                    {codebase.name} {codebase.indexStatus === 'complete' ? '✓' : '⏳'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ZIP Download Button */}
          {currentConversation && (
            <button
              onClick={handleDownloadZip}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
              title="Download conversation files as ZIP"
            >
              <Archive className="w-4 h-4" />
              ZIP
            </button>
          )}

          <div className="flex items-end gap-2 max-w-4xl mx-auto">
            <button className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors">
              <Plus className="w-5 h-5" />
            </button>
            
            <div className="flex-1 relative">
              <textarea
                key={inputKey}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder="Ask HalCode anything..."
                className="w-full bg-dark-700/90 backdrop-blur-sm border border-dark-600 rounded-xl px-5 py-3.5 pr-14 text-dark-100 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/30 resize-none max-h-48 transition-all duration-200"
                rows={1}
                style={{ minHeight: '52px' }}
              />
              
              <div className="absolute right-3 bottom-3 flex items-center gap-1.5">
                {isStreaming && (
                  <button 
                    onClick={cancelStreaming}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleSend}
                  disabled={!message.trim() || isStreaming}
                  className="p-1.5 text-primary-400 hover:text-primary-300 hover:bg-primary-500/10 disabled:text-dark-500 rounded-lg transition-all disabled:hover:bg-transparent"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* <p className="text-xs text-dark-500 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p> */}
        </div>
      </div>
    </div>
  )
}
